import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack
} from '@chakra-ui/react'
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'
import { FiPlus, FiTrash2, FiUsers } from 'react-icons/fi'

import { useAppToast } from '../../../helpers'
import ContestEvaluatorsService from '../../../services/Contests/ContestEvaluatorsService'
import { IContestEvaluator } from '../../../services/Contests/interfaces'

interface IEvaluatorsManagementModalProps {
  isOpen: boolean
  onClose: () => void
  contestId: number | string
  contestName: string
  initialEvaluators: IContestEvaluator[]
  required: boolean
  onSaved: (evaluators: IContestEvaluator[]) => void
}

interface IEditableRow {
  key: string
  id?: number
  name: string
  error?: string
}

function makeRow(evaluator?: IContestEvaluator): IEditableRow {
  return {
    key: evaluator ? `id-${evaluator.id}` : `new-${Math.random().toString(36).slice(2, 9)}`,
    id: evaluator?.id,
    name: evaluator?.name || ''
  }
}

export default function EvaluatorsManagementModal({
  isOpen,
  onClose,
  contestId,
  contestName,
  initialEvaluators,
  required,
  onSaved
}: IEvaluatorsManagementModalProps) {
  const service = useMemo(() => new ContestEvaluatorsService(), [])
  const toast = useAppToast()

  const [rows, setRows] = useState<IEditableRow[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const focusTargetRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    if (initialEvaluators.length === 0) {
      setRows([makeRow()])
    } else {
      setRows(initialEvaluators.map((e) => makeRow(e)))
    }
  }, [isOpen, initialEvaluators])

  useEffect(() => {
    if (!focusTargetRef.current) return
    const target = inputRefs.current[focusTargetRef.current]
    if (target) {
      target.focus()
      target.select()
    }
    focusTargetRef.current = null
  }, [rows])

  const handleAdd = () => {
    const newRow = makeRow()
    focusTargetRef.current = newRow.key
    setRows((prev) => [...prev, newRow])
  }

  const handleRemove = (key: string) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.key === key)
      const next = prev.filter((r) => r.key !== key)
      const fallback = next[idx] || next[idx - 1]
      if (fallback) focusTargetRef.current = fallback.key
      delete inputRefs.current[key]
      return next
    })
  }

  const handleNameChange = (key: string, name: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, name, error: undefined } : r)))
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, key: string) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      const currentRow = rows.find((r) => r.key === key)
      if (!currentRow || !currentRow.name.trim()) return

      const idx = rows.findIndex((r) => r.key === key)
      const isLast = idx === rows.length - 1

      if (isLast) {
        handleAdd()
      } else {
        focusTargetRef.current = rows[idx + 1].key
        setRows((prev) => [...prev])
      }
      return
    }

    if (event.key === 'ArrowDown') {
      const idx = rows.findIndex((r) => r.key === key)
      if (idx < rows.length - 1) {
        event.preventDefault()
        focusTargetRef.current = rows[idx + 1].key
        setRows((prev) => [...prev])
      }
      return
    }

    if (event.key === 'ArrowUp') {
      const idx = rows.findIndex((r) => r.key === key)
      if (idx > 0) {
        event.preventDefault()
        focusTargetRef.current = rows[idx - 1].key
        setRows((prev) => [...prev])
      }
      return
    }

    if (event.key === 'Backspace') {
      const currentRow = rows.find((r) => r.key === key)
      if (currentRow && currentRow.name === '' && rows.length > 1) {
        event.preventDefault()
        handleRemove(key)
      }
    }
  }

  const validate = (): boolean => {
    let valid = rows.length > 0
    const nextRows = rows.map((r) => {
      if (!r.name.trim()) {
        valid = false
        return { ...r, error: 'Nome obrigatório' }
      }
      return { ...r, error: undefined }
    })
    setRows(nextRows)
    return valid
  }

  const handleSave = async () => {
    if (!validate()) return

    setIsSaving(true)
    try {
      const result = await service.sync(contestId, {
        evaluators: rows.map((r) => ({ id: r.id, name: r.name.trim() }))
      })
      toast.success('Avaliadores salvos com sucesso')
      onSaved(result.data)
      onClose()
    } catch {
      toast.error('Erro ao salvar avaliadores')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    if (required && initialEvaluators.length === 0) return
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      closeOnEsc={!required || initialEvaluators.length > 0}
      closeOnOverlayClick={!required || initialEvaluators.length > 0}
      isCentered
    >
      <ModalOverlay bg="rgba(17,24,39,0.6)" backdropFilter="blur(4px)" />
      <ModalContent
        borderRadius="16px"
        overflow="hidden"
        bg="white"
        fontFamily="Inter, sans-serif"
        boxShadow="0 24px 60px -12px rgba(17,24,39,0.25)"
      >
        {!required || initialEvaluators.length > 0 ? (
          <ModalCloseButton
            zIndex="2"
            bg="rgba(255,255,255,0.9)"
            borderRadius="full"
            size="sm"
            top="12px"
            right="12px"
            _hover={{ bg: 'white' }}
          />
        ) : null}

        <ModalHeader px="6" pt="6" pb="2">
          <Flex align="center" gap="3" mb="1">
            <Flex
              align="center"
              justify="center"
              w="40px"
              h="40px"
              borderRadius="10px"
              bg="magenta.50"
              color="magenta.500"
              fontSize="18px"
            >
              <FiUsers />
            </Flex>
            <Box>
              <Text fontSize="18px" fontWeight="700" color="gray.900" letterSpacing="-0.01em">
                Avaliadores do concurso
              </Text>
              <Text fontSize="12px" color="gray.500" fontWeight="400">
                {contestName}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>

        <ModalBody px="6" pb="2">
          {required && initialEvaluators.length === 0 && (
            <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="10px" p="3" mb="4">
              <Text fontSize="13px" color="orange.800" fontWeight="500">
                Cadastre pelo menos um avaliador para começar a avaliar as obras deste concurso.
              </Text>
            </Box>
          )}

          <VStack spacing="2.5" align="stretch">
            {rows.map((row, index) => (
              <Flex key={row.key} align="flex-start" gap="2">
                <Box flex="1">
                  <Input
                    ref={(el) => {
                      inputRefs.current[row.key] = el
                    }}
                    value={row.name}
                    onChange={(e) => handleNameChange(row.key, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, row.key)}
                    placeholder={`Nome do avaliador ${index + 1}`}
                    borderRadius="10px"
                    borderColor={row.error ? 'red.400' : 'gray.200'}
                    fontSize="14px"
                    h="42px"
                    bg="white"
                    autoComplete="off"
                    _placeholder={{ color: 'gray.400' }}
                    _hover={{ borderColor: row.error ? 'red.500' : 'gray.300' }}
                    _focus={{
                      borderColor: row.error ? 'red.500' : 'magenta.500',
                      boxShadow: row.error ? '0 0 0 3px rgba(239,68,68,0.12)' : '0 0 0 3px rgba(250,0,105,0.12)'
                    }}
                  />
                  {row.error && (
                    <Text fontSize="11px" color="red.500" mt="1">
                      {row.error}
                    </Text>
                  )}
                </Box>
                <IconButton
                  aria-label="Remover avaliador"
                  icon={<FiTrash2 />}
                  variant="ghost"
                  color="gray.500"
                  borderRadius="10px"
                  h="42px"
                  w="42px"
                  isDisabled={rows.length === 1 && required && initialEvaluators.length === 0}
                  onClick={() => handleRemove(row.key)}
                  _hover={{ bg: 'red.50', color: 'red.500' }}
                />
              </Flex>
            ))}
          </VStack>

          <Button
            onClick={handleAdd}
            leftIcon={<FiPlus />}
            mt="3"
            variant="ghost"
            color="magenta.500"
            fontSize="13px"
            fontWeight="600"
            borderRadius="10px"
            _hover={{ bg: 'magenta.50' }}
          >
            Adicionar avaliador
          </Button>
        </ModalBody>

        <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.200" gap="2" px="6" py="4">
          {(!required || initialEvaluators.length > 0) && (
            <Button
              variant="ghost"
              onClick={onClose}
              fontSize="14px"
              fontWeight="600"
              color="gray.600"
              borderRadius="10px"
              _hover={{ bg: 'gray.100' }}
            >
              Cancelar
            </Button>
          )}
          <Button
            bg="magenta.500"
            color="white"
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Salvando..."
            isDisabled={rows.length === 0}
            fontSize="14px"
            fontWeight="600"
            borderRadius="10px"
            px="6"
            _hover={{ bg: 'magenta.600' }}
            _active={{ transform: 'scale(0.98)' }}
            sx={{ transition: 'all .15s ease' }}
          >
            Salvar avaliadores
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
